import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Globe, Users, Plus, Trash2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SettingsPage({ user, token, onUserUpdate }) {
  const { t, i18n } = useTranslation();
  const [language, setLanguage] = useState(user.preferred_language || 'en');
  const [saving, setSaving] = useState(false);
  const [children, setChildren] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [childData, setChildData] = useState({
    full_name: '',
    teoudat_zehout: '',
    birth_date: '',
    school_name: ''
  });

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'he', name: 'עברית' },
    { code: 'fr', name: 'Français' },
    { code: 'es', name: 'Español' },
    { code: 'ru', name: 'Русский' },
    { code: 'ar', name: 'العربية' },
    { code: 'it', name: 'Italiano' },
    { code: 'pt', name: 'Português' },
    { code: 'zh', name: '中文' },
    { code: 'uk', name: 'Українська' }
  ];

  useEffect(() => {
    fetchChildren();
  }, []);

  const fetchChildren = async () => {
    try {
      const response = await axios.get(`${API}/children`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChildren(response.data);
    } catch (error) {
      console.error('Failed to load children:', error);
    }
  };

  const handleAddChild = async () => {
    try {
      await axios.post(`${API}/children`, childData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Child added successfully');
      setDialogOpen(false);
      setChildData({ full_name: '', teoudat_zehout: '', birth_date: '', school_name: '' });
      fetchChildren();
    } catch (error) {
      toast.error('Failed to add child');
    }
  };

  const handleRemoveChild = async (childId) => {
    try {
      await axios.delete(`${API}/children/${childId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Child removed successfully');
      fetchChildren();
    } catch (error) {
      toast.error('Failed to remove child');
    }
  };

  const handleLanguageChange = (newLang) => {
    setLanguage(newLang);
    // Immediately preview the language change
    i18n.changeLanguage(newLang);
    localStorage.setItem('selected_language', newLang);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/user/settings`,
        { preferred_language: language },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local user data
      const updatedUser = { ...user, preferred_language: language };
      onUserUpdate(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      // Change app language
      i18n.changeLanguage(language);
      
      toast.success(t('save_changes'));
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight" data-testid="settings-title">
          {t('settings')}
        </h1>
        <p className="text-lg text-slate-600">{t('manage_preferences')}</p>
      </div>

      {/* User Info Card */}
      <Card className="p-8 bg-white rounded-2xl border border-slate-200">
        <h2 className="text-2xl font-bold text-slate-900 mb-6">{t('profile_info')}</h2>
        <div className="space-y-4">
          <div>
            <Label className="text-slate-600">{t('full_name')}</Label>
            <p className="text-xl font-semibold text-slate-900 mt-1" data-testid="settings-full-name">{user.full_name}</p>
          </div>
          <div>
            <Label className="text-slate-600">{t('teoudat_zehout')}</Label>
            <p className="text-xl font-semibold text-slate-900 mt-1" data-testid="settings-teoudat-zehout">{user.teoudat_zehout}</p>
          </div>
        </div>
      </Card>

      {/* Language Settings */}
      <Card className="p-8 bg-white rounded-2xl border border-slate-200">
        <div className="flex items-center gap-3 mb-6">
          <Globe className="h-6 w-6 text-blue-600" />
          <h2 className="text-2xl font-bold text-slate-900">{t('language')}</h2>
        </div>

        <div className="space-y-4">
          <div>
            <Label className="text-base font-semibold mb-2 block">{t('language')}</Label>
            <Select value={language} onValueChange={handleLanguageChange}>
              <SelectTrigger className="h-12 rounded-xl" data-testid="settings-language-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {languages.map((lang) => (
                  <SelectItem key={lang.code} value={lang.code}>
                    {lang.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button
            onClick={handleSave}
            disabled={saving || language === user.preferred_language}
            className="w-full h-12 text-base font-semibold rounded-full gradient-blue hover:opacity-90 transition-all shadow-lg shadow-blue-600/30"
            data-testid="save-settings-button"
          >
            <Save className="h-5 w-5 mr-2" />
            {saving ? t('saving') : t('save_changes')}
          </Button>
        </div>
      </Card>

      {/* Children Management */}
      <Card className="p-8 bg-white rounded-2xl border border-slate-200">
        <div className="flex items-center gap-3 mb-6">
          <Users className="h-6 w-6 text-blue-600" />
          <h2 className="text-2xl font-bold text-slate-900">{t('my_children')}</h2>
        </div>

        <div className="space-y-4">
          {children.map((child) => (
            <div key={child.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200">
              <div>
                <p className="font-semibold text-slate-900">{child.full_name}</p>
                <p className="text-sm text-slate-600">{t('child_teoudat')}: {child.teoudat_zehout}</p>
                <p className="text-sm text-slate-600">{t('school_name')}: {child.school_name}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleRemoveChild(child.id)}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}

          {children.length === 0 && (
            <p className="text-center text-slate-500 py-8">{t('no_children_yet')}</p>
          )}

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="w-full rounded-full bg-blue-600 hover:bg-blue-700">
                <Plus className="h-4 w-4 mr-2" />
                {t('add_child')}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t('add_child')}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label>{t('child_name')}</Label>
                  <Input
                    value={childData.full_name}
                    onChange={(e) => setChildData({ ...childData, full_name: e.target.value })}
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label>{t('child_teoudat')}</Label>
                  <Input
                    value={childData.teoudat_zehout}
                    onChange={(e) => setChildData({ ...childData, teoudat_zehout: e.target.value })}
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label>{t('birth_date')}</Label>
                  <Input
                    type="date"
                    value={childData.birth_date}
                    onChange={(e) => setChildData({ ...childData, birth_date: e.target.value })}
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label>{t('school_name')}</Label>
                  <Input
                    value={childData.school_name}
                    onChange={(e) => setChildData({ ...childData, school_name: e.target.value })}
                    className="mt-2"
                  />
                </div>
                <Button onClick={handleAddChild} className="w-full rounded-full bg-blue-600 hover:bg-blue-700">
                  {t('add_child')}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </Card>
    </div>
  );
}